// src/utils/localStorage_util.ts

// Clés de stockage
const STORAGE_KEYS = {
  REGISTRATION_SESSION: 'registrationSessionToken',

  // Ajoutez d'autres clés selon vos besoins
};

/**
 * localStorageUtil - Gestionnaire unifié du localStorage
 */
export const localStorageUtil = {
  /**
   * Sauvegarder une valeur
   * @param {string} key - Clé de stockage
   * @param {any} value - Valeur à sauvegarder
   */
  set(key: string, value: any): void {
    try {
      const serializedValue = typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`Erreur lors de la sauvegarde de ${key}:`, error);
    }
  },

  /**
   * Récupérer une valeur
   * @param {string} key - Clé de stockage
   * @param {any} defaultValue - Valeur par défaut si non trouvée
   * @returns {any}
   */
  get(key: string, defaultValue: any = null): any {
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;

      // Tentative de parsing JSON
      try {
        return JSON.parse(value);
      } catch {
        // Si ce n'est pas du JSON, retourner la valeur brute
        return value;
      }
    } catch (error) {
      console.error(`Erreur lors de la récupération de ${key}:`, error);
      return defaultValue;
    }
  },

  /**
   * Supprimer une clé spécifique
   * @param {string} key - Clé à supprimer
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
      console.log(`✅ ${key} supprimé du localStorage`);
    } catch (error) {
      console.error(`Erreur lors de la suppression de ${key}:`, error);
    }
  },

  /**
   * Supprimer plusieurs clés
   * @param {string[]} keys - Tableau de clés à supprimer
   */
  removeMultiple(keys: string[]): void {
    keys.forEach(key => this.remove(key));
  },

  /**
   * Vider tout le localStorage
   */
  clear(): void {
    try {
      localStorage.clear();
      console.log('✅ localStorage complètement vidé');
    } catch (error) {
      console.error('Erreur lors du vidage du localStorage:', error);
    }
  },

  /**
   * Vérifier si une clé existe
   * @param {string} key - Clé à vérifier
   * @returns {boolean}
   */
  has(key: string): boolean {
    return localStorage.getItem(key) !== null;
  },

  /**
   * Récupérer toutes les clés
   * @returns {string[]}
   */
  keys(): string[] {
    return Object.keys(localStorage);
  },

  /**
   * Récupérer la taille approximative du localStorage en octets
   * @returns {number}
   */
  size(): number {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        total += (key.length + (value?.length || 0)) * 2; // Approximation UTF-16
      }
    }
    return total;
  },

  // Constantes pour éviter les erreurs de frappe
  KEYS: STORAGE_KEYS
};

// Exemple d'utilisation dans votre composant
/*
import { localStorageUtil } from "shared/lib/localStorage_util";

// Sauvegarder
localStorageUtil.set(localStorageUtil.KEYS.REGISTRATION_SESSION, 'token123');

// Récupérer
const token = localStorageUtil.get(localStorageUtil.KEYS.REGISTRATION_SESSION);

// Supprimer spécifiquement le token de session
localStorageUtil.remove(localStorageUtil.KEYS.REGISTRATION_SESSION);

// Supprimer plusieurs clés
localStorageUtil.removeMultiple([
  localStorageUtil.KEYS.REGISTRATION_SESSION,
  localStorageUtil.KEYS.USER_PREFERENCES
]);

// Vider tout
localStorageUtil.clear();
*/